import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { renderWithMobileBreaks, renderWithAllBreaks } from "../utils/mobileBreaks";

// Warm candlelight-on-wooden-surface photograph — Unsplash / Elijah Pilchard.
// Soft, intimate, wabi-sabi aesthetic that harmonizes with parchment tones.
const POLAROID_PHOTO_URL =
  "https://images.unsplash.com/photo-1734235861797-f6fbe3da8e71?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400";

export const EnvelopePreview = ({ duration = 365 }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pulseCue, setPulseCue] = useState(false);
  const envelopeRef = useRef(null);
  const isZh = i18n.language.startsWith("zh");
  const sample = t("preview_hero.letter_sample");
  const replyBlur = t("preview_hero.reply_blur");

  // ── Session-scoped "already sealed" gate ────────────────────────────
  // If this browser session has already sealed a real letter (flag set
  // on /success), we swap the interactive sample-letter demo for a
  // subtle confirmation strip. Prevents the "why is my letter blurred /
  // still a sample?" confusion for users who return to the landing page
  // after paying.
  const [sealedRecord, setSealedRecord] = useState(null);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("mp-sealed");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.capsule_id) setSealedRecord(parsed);
    } catch { /* ignore */ }
  }, []);

  // One-time wax-seal pulse when the envelope first scrolls into view
  useEffect(() => {
    if (!envelopeRef.current || pulseCue) return;
    const node = envelopeRef.current;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPulseCue(true);
          io.disconnect();
        }
      },
      { threshold: 0.45 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [pulseCue]);

  // ── Sealed-confirmation strip (short-circuit render) ────────────────
  // When a real letter has already been sealed in this session, replace
  // the full sample-letter envelope demo with a small, quiet parchment
  // strip that reassures the user their memory is under wax.
  if (sealedRecord) {
    const days = Number(sealedRecord.duration_days) || duration;
    return (
      <section
        className="relative pt-8 pb-24 md:pb-32"
        data-testid="sealed-confirmation"
      >
        <div className="max-w-2xl mx-auto px-6 text-center">
          <p className="text-[11px] uppercase tracking-[0.42em] text-[var(--wax)] mb-6">
            — 01 —
          </p>
          <img
            src="/press-kit/logo-192.png"
            alt=""
            aria-hidden="true"
            draggable={false}
            width="64"
            height="64"
            style={{
              width: 64,
              height: 64,
              objectFit: "contain",
              margin: "0 auto",
              display: "block",
              imageRendering: "-webkit-optimize-contrast",
              mixBlendMode: "multiply",
              filter:
                "drop-shadow(0 2px 4px rgba(60,20,15,0.22)) drop-shadow(0 10px 22px rgba(60,20,15,0.12))",
              transform: "rotate(-4deg)",
            }}
          />
          <p
            className="mt-6 font-display italic text-[19px] md:text-[22px] text-[var(--ink)] leading-relaxed"
            data-testid="sealed-confirmation-line"
          >
            {t("preview_hero.sealed_confirmation", { days })}
          </p>
          <p className="mt-4 text-[12px] uppercase tracking-[0.28em] text-[var(--ink-mute)]">
            {t("preview_hero.sealed_sub")}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="relative pt-8 pb-24 md:pb-32">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="text-[11px] uppercase tracking-[0.42em] text-[var(--wax)] mb-4">
            — 01 —
          </p>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight" data-testid="preview-hero-title">
            {renderWithMobileBreaks(t("preview_hero.kicker"))}
          </h2>
          <p className="mt-4 text-[var(--ink-soft)] italic max-w-xl mx-auto">
            {isZh ? renderWithMobileBreaks(t("preview_hero.subtitle")) : renderWithAllBreaks(t("preview_hero.subtitle"))}
          </p>
        </div>

        <div
          ref={envelopeRef}
          className={`envelope ${open ? "open" : ""} ${pulseCue ? "pulse-cue" : ""}`}
          onClick={() => setOpen((v) => !v)}
          role="button"
          tabIndex={0}
          aria-pressed={open}
          aria-label={open ? t("preview_hero.reseal_cta") : t("preview_hero.open_cta")}
          data-testid="envelope-preview"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpen((v) => !v);
            }
          }}
        >
          <div className="envelope-inner">
            <div className="envelope-flap" />
            <div className="envelope-wax" data-testid="envelope-wax">
              <img
                src="/press-kit/badge-seal-192.png"
                alt="SEAL"
                width="56"
                height="56"
                draggable={false}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  imageRendering: "-webkit-optimize-contrast",
                  pointerEvents: "none",
                }}
              />
            </div>
            <div className="envelope-address">
              <span className="envelope-to-label">{t("preview_hero.to_label")}</span>
              <span className="block font-display text-[12px] sm:text-[13px] tracking-[0.42em] uppercase text-[var(--ink)] not-italic mt-2" data-testid="envelope-addressee">
                {t("preview_hero.addressee")}
              </span>
            </div>

            <article className="envelope-letter" data-testid="envelope-letter">
              <div className="h-full overflow-hidden pt-6 px-6 pb-14 md:pt-8 md:px-8 md:pb-16 relative">
                <div className="flex items-baseline justify-between mb-4">
                  <span className="font-display italic text-[11px] tracking-[0.32em] uppercase text-[var(--wax)]" data-testid="envelope-brand">
                    {t("nav.brand")} · {duration}·D
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--ink-soft)]" data-testid="envelope-sent">
                    {t("preview_hero.from_label")} · {duration}D
                  </span>
                </div>

                <div className="flex gap-4 items-start">
                  <div
                    className="hidden md:block polaroid -rotate-3 shrink-0"
                    style={{ width: 108 }}
                    data-testid="letter-polaroid"
                  >
                    <img
                      src={POLAROID_PHOTO_URL}
                      alt=""
                      aria-hidden="true"
                      draggable={false}
                      className="candle-breathe"
                      data-testid="letter-polaroid-img"
                      style={{
                        width: "100%",
                        height: 108,
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-display italic text-[10px] uppercase tracking-[0.34em] text-[var(--wax)] mb-2">
                      {t("preview_hero.letter_heading")}
                    </p>
                    <p
                      className={`text-[14px] md:text-[15px] ${
                        isZh ? "font-hand-cn" : "font-hand"
                      } text-[var(--ink)]`}
                      style={
                        isZh
                          ? { fontSize: 16, lineHeight: 1.8, letterSpacing: "0.02em" }
                          : { fontSize: 18, lineHeight: 1.7, letterSpacing: "0.005em" }
                      }
                      data-testid="envelope-sample-letter"
                    >
                      {sample}
                    </p>
                  </div>
                </div>

                {/* Dedicated signature strip — ABSOLUTELY positioned in the
                    bottom-right corner of the letter body with an explicit
                    safe-zone offset. The letter body reserves pb-14/pb-16
                    of bottom padding above so text can never spill into
                    this lane, even when localised strings (zh-HK / zh-TW)
                    run 7+ lines vs English's 5. This guarantees a stable
                    ≥24px paper-margin on all sides of the seal in every
                    locale + viewport. */}
                <div
                  className="absolute right-5 bottom-4 md:right-8 md:bottom-6 flex justify-end items-end pointer-events-none"
                  data-testid="letter-signature-row"
                >
                  <img
                    src="/press-kit/logo-192.png"
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                    data-testid="letter-signature-seal"
                    className="pointer-events-none select-none"
                    style={{
                      width: 44,
                      height: 44,
                      objectFit: "contain",
                      imageRendering: "-webkit-optimize-contrast",
                      mixBlendMode: "multiply",
                      transform: "rotate(-6deg)",
                    }}
                  />
                </div>
              </div>
            </article>
          </div>
        </div>

        <div className="text-center mt-10 envelope-toggle-row" data-open={open}>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="envelope-toggle-btn"
            className="text-[12px] uppercase tracking-[0.22em] text-[var(--ink-soft)] hover:text-[var(--wax)] transition-colors"
          >
            {open ? t("preview_hero.reseal_cta") : t("preview_hero.open_cta")} →
          </button>
        </div>
      </div>
    </section>
  );
};
